import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {TreeResolver} from "../tree-resolver.service";
import {BuildingStructure} from "../../model/building-structure";
import {Localization} from "../../../../common/enums/localization";
import {StructureType} from "../../../../common/enums/enums";
import {BuildingsService} from "../../../../common/services/buildings.service";
import {BuildingSummary} from "../../../../common/model/building-summary";
import {UntypedFormControl} from "@angular/forms";
import {pluck} from "../../../../../helpers/collections";
import {StructureService} from "../../structure.service";

function summaryToStructure(summary: BuildingSummary): BuildingStructure {
    return summary as unknown as BuildingStructure
}

@Component({
    selector: 'app-structure-location-tree',
    templateUrl: './structure-location-tree.component.html',
    styleUrls: ['./structure-location-tree.component.scss']
})
export class StructureLocationTreeComponent implements OnChanges {

    @Input()
    public structureId: number;
    @Input()
    public rootType: RootType = StructureType.SITE;
    @Input()
    public control: UntypedFormControl;

    /**
     *
     */
    @Input()
    public accepts: StructureType[] = Object.values(StructureType);
    @Input()
    public endAt: StructureType[] = [];

    /**
     * Group types can never be the
     * selected structure node
     */
    private readonly excludes: StructureType[] = [];


    public location: StructureSelectionNode[] = [];


    constructor(
        private readonly resolver: TreeResolver,
        private readonly structureResource: StructureService,
        private readonly buildingsResource: BuildingsService,
    ) {
    }

    public get selectedNode(): BuildingStructure {
        const selections = this.location
            .map(node => node.selection)
            .filter(Boolean);
        return selections[selections.length -1];
    }

    public get hasSelection() {
        return !!this.selectedNode;
    }


    public get isValidSelection() {
        return !this.excludes.includes(this.selectedNode?.type)
        && this.accepts.includes(this.selectedNode?.type);
    }

    public get isInvalidSelection() {
        return this.hasSelection && !this.isValidSelection;
    }


    async ngOnChanges(changes:SimpleChanges): Promise<void> {
            await this.setupStructure();
        if (changes.structureId) {
            console.log(this.location);
        }
    }


    public async siblingsOf(structure: BuildingStructure) {
        const options = await (
            structure.type === StructureType.BUILDING ?
                this.buildingsAsStructures() :
                this.structureResource.childrenFor(structure.parentStructureId).toPromise()
        );
        structure.type = options[0]?.type; // @todo not sure if this the proper way
        return options;
    }


    private async setupStructure() {
        if (!this.structureId) {
            await this.setupEmptyStructure();
            return console.info('Cannot determine structure without a building ID');
        }

        const location = (await this.resolver.sequenceFor(this.structureId))
            .map(structure => ({selection: structure, options: null} as StructureSelectionNode));

        for (let segment of location) {
            segment.options = await this.siblingsOf(segment.selection);
        }

        this.location = location;

        const lastNodeInLocation = this.location[this.location.length -1];
        if (lastNodeInLocation.selection) {
            await this.didChangeSelection(lastNodeInLocation.selection, this.location.indexOf(lastNodeInLocation));
        }
    }

    protected readonly Localization = Localization;

    private async buildingsAsStructures(): Promise<BuildingStructure[]> {
        const buildings = await this.buildingsResource.getAllBuildings().toPromise();
        return buildings.map(summaryToStructure)
    }


    /**
     * Whenever the selection of one of the nodes changes
     * there are three important things that need to be done.
     * - All the selected nodes after this one are no longer
     * valid and need to be removed
     * - We need to get the child structures for the newly
     * selected node
     * - When there are child nodes we can add another
     * node for selection, with those child nodes as
     * it's options.
     * @param selection The structure that was selected from the dropdown
     * @param position
     */
    public async didChangeSelection(selection: BuildingStructure, position: number) {

        // Clean up everything after this selection
        this.location.length = position + 1;
        const isFinalNode = this.endAt.includes(selection.type);

        if (selection) {
            const optionsForNextNode = await this.structureResource.childrenFor(selection.id).toPromise();
            if (optionsForNextNode.length && !isFinalNode) {
                this.location.push({
                    selection: null,
                    options: optionsForNextNode,
                });
            }
        }

        this.control.setValue(
            this.isValidSelection ? this.selectedNode : null
        );
    }


    private async setupEmptyStructure() {
        const allRootNodes = await this.buildingsAsStructures();
        this.location = [{
            selection: null,
            options: allRootNodes,
            config: { groupBy: 'type'}
        } as StructureSelectionNode];
    }

    public uniqueTypesIn(options: BuildingStructure[]) {
        return Array.from(new Set(options.map(pluck('type'))));
    }

    public placeholdersFor(options: BuildingStructure[]) {
        return this.uniqueTypesIn(options)
            .map((type: string) => type.replace(/elementgroup/i, ''));
    }
}

interface StructureSelectionNode {
    selection: BuildingStructure,
    options: BuildingStructure[],
    config?: {
        groupBy?: keyof BuildingStructure,
    }
}

type RootType = StructureType.SITE | StructureType.BUILDING;
